/**
 * La taille du **bitmap** de la zone de signature — c'est-à-dire le nombre de pixels que le
 * `<canvas>` enregistre réellement, indépendamment de la place qu'il occupe à l'écran.
 *
 * ⚠️ Ce qui se joue ici est la **proportion**, pas la finesse : un bitmap qui n'a pas le rapport
 * largeur/hauteur de la zone où l'agent trace enregistre un paraphe comprimé — une signature
 * large de 900 px CSS rangée dans un bitmap de 640 s'écrase d'environ 30 % (#171). La netteté
 * n'est qu'un effet de bord de la densité d'écran.
 *
 * Isolé du composant parce que c'est la seule partie vérifiable sans contexte 2D : jsdom n'en
 * a pas, et toutes les règles qui se paient cher quand on les rate (plafond, seuil, repli,
 * arrondi, épaisseur du trait) sont ici.
 */

/**
 * Plafond de **chaque côté** du bitmap. Au-delà, on ne gagne rien de visible et le PNG enfle :
 * 2000 × 462 pèse déjà ~48 Kio.
 *
 * ⚠️ Il borne bien les deux côtés, et pas seulement la largeur. Le CSS fixe aujourd'hui
 * `block-size: 13rem`, donc une zone haute et étroite n'existe pas — mais un plafond qui
 * annonce protéger la mémoire du canvas et ne regarde qu'une dimension est un piège pour le
 * prochain lecteur : une boîte de 100 × 20000 à densité 2 donnerait 200 × 40000, soit 32 Mo de
 * mémoire de rendu, un canvas blanc et un agent qui ne peut plus signer.
 */
export const SIGNATURE_MAX_SIDE = 2000

/** Repli quand la boîte n'est pas exploitable (premier rendu, jsdom) — l'ancien format. */
export const SIGNATURE_FALLBACK = { width: 640, height: 220 } as const

/**
 * En deçà, le bitmap calculé ne peut plus rien porter et on lui préfère le repli.
 *
 * ⚠️ Le seuil n'est pas une précaution de style, c'est ce qui sépare « petit » d'« inexploitable ».
 * Sans lui, une boîte de 0,2 × 0,2 — un enfant flex momentanément écrasé, une mesure prise
 * pendant une transition — passait pour mesurée : le plancher à 1 px rendait un bitmap de 1 × 1,
 * l'agent signait, et `toBlob` émettait **un PNG transparent** que la fiche emportait comme
 * signature. Ni refus ni avertissement sur un acte irréversible dont la donnée ne se reconstitue
 * pas — l'inverse du garde-fou 3.
 */
export const SIGNATURE_MIN_SIDE = 16

/** Le trait valait 2,5 px sur un bitmap de 640 : on garde ce rapport à toute taille. */
export const SIGNATURE_LINE_RATIO = 2.5 / 640

export interface SignatureBitmap {
  width: number
  height: number
  lineWidth: number
  /**
   * Pixels de bitmap par pixel CSS — l'échelle réellement appliquée.
   *
   * ⚠️ C'est elle, et **jamais la forme de la boîte**, qui dit comment reporter l'encre déjà
   * posée sur un bitmap redimensionné. La hauteur de la zone est figée par le CSS et sa largeur
   * suit le conteneur : redimensionner en repeignant l'encre « à la nouvelle taille » l'étire
   * donc horizontalement à chaque changement de largeur — le paraphe déformé que cette fiche
   * répare. À densité constante, l'échelle ne bouge pas : l'encre reste où l'agent l'a tracée,
   * et le canvas gagne ou perd des pixels sur sa droite.
   */
  scale: number
}

/**
 * L'état de tracé de la zone, le seul qui décide si le bitmap peut être reposé maintenant.
 */
export interface SignatureStrokeState {
  /** Un pointeur est en train de tracer : le geste a commencé, il n'est pas relâché. */
  drawing: boolean
}

/**
 * Peut-on (re)poser la taille du bitmap maintenant ?
 *
 * ⚠️ **Écrire `canvas.width` efface le canvas *et* réinitialise son contexte 2D — même en y
 * remettant la valeur qu'il portait déjà.** `lineWidth` y retombe à 1 et `lineCap` à `butt` :
 * un redimensionnement arrivé entre le `pointerdown` et le premier `pointermove` fait sortir
 * tout le paraphe en trait de 1 px, sans rien effacer de visible. **Un geste en cours est donc
 * le seul motif de refuser la mesure**, et il est temporaire par nature — le relâchement rouvre.
 *
 * ⚠️ Un paraphe **déjà posé**, lui, ne ferme plus rien, et c'est un changement de doctrine qu'il
 * faut lire : le bloquer échangeait une perte contre une autre. On ne perdait plus l'encre, on
 * perdait sa proportion — exactement ce que #171 répare. Une tablette tournée après le tracé
 * gardait un bitmap 1800 × 416 dans une zone 600 × 208, et `pointOf()` posait alors les traits
 * suivants à une échelle différente des précédents **dans la même image**. Le composant
 * redimensionne donc et **repeint l'encre** (`SignaturePad.tsx`) : les deux branches se ferment.
 */
export function canMeasureSignature({ drawing }: SignatureStrokeState): boolean {
  return !drawing
}

/**
 * Taille du bitmap pour une boîte CSS donnée et une densité d'écran donnée.
 *
 * ⚠️ C'est la **proportion** qui compte, pas la définition : un bitmap qui n'a pas le rapport
 * de la boîte où l'agent signe enregistre un paraphe comprimé (#171).
 *
 * ⚠️ La boîte attendue est la **boîte de contenu** (`clientWidth` / `clientHeight`), jamais celle
 * que rend `getBoundingClientRect()`. Le canvas porte une bordure de 1,5 px en `border-box` : la
 * boîte de bordure fait 900 × 208 quand le bitmap est peint dans 897 × 205, soit 1,1 % d'erreur
 * de proportion — l'objet même de cette fiche.
 */
export function signatureBitmapFor(
  box: { width: number; height: number },
  devicePixelRatio: number,
): SignatureBitmap {
  if (!isMeasured(box.width) || !isMeasured(box.height)) {
    return fallbackBitmap()
  }

  // Le plafond porte sur les deux côtés, et le `min` avec la densité est ce qui l'empêche
  // d'agrandir une petite zone : une boîte de 300 px reste à sa densité d'écran, elle ne monte
  // pas à 2000.
  const scale = Math.min(
    densityOf(devicePixelRatio),
    SIGNATURE_MAX_SIDE / box.width,
    SIGNATURE_MAX_SIDE / box.height,
  )

  const width = box.width * scale
  const height = box.height * scale

  // Un bitmap dégénéré vaut une zone non mesurée : le repli garde une zone utilisable là où le
  // plancher à 1 px rendait un PNG vide accepté comme signature (cf. `SIGNATURE_MIN_SIDE`).
  if (width < SIGNATURE_MIN_SIDE || height < SIGNATURE_MIN_SIDE) {
    return fallbackBitmap()
  }

  return bitmapOf(width, height, scale)
}

/**
 * Une boîte non mesurée vaut `0 × 0` (canvas pas encore disposé, jsdom), et un écran replié
 * ou détaché peut rendre `NaN`. Aucune de ces valeurs ne donne un bitmap utilisable.
 */
function isMeasured(side: number): boolean {
  return Number.isFinite(side) && side > 0
}

function densityOf(devicePixelRatio: number): number {
  return Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1
}

function bitmapOf(width: number, height: number, scale: number): SignatureBitmap {
  // Un canvas n'accepte que des entiers : `canvas.width = 1350.75` tronque silencieusement.
  const pixelWidth = Math.round(width)

  return {
    width: pixelWidth,
    height: Math.round(height),
    lineWidth: pixelWidth * SIGNATURE_LINE_RATIO,
    scale,
  }
}

/**
 * Le repli ne décrit aucune boîte : son échelle est donc **1**, et non un rapport calculé.
 *
 * Ce n'est pas un défaut à corriger plus tard. Un repli n'arrive qu'avant la première mesure,
 * quand rien n'est encore tracé : il n'y a pas d'encre à reporter, donc pas d'échelle qui
 * compte. Lui inventer une densité ferait croire l'inverse au prochain lecteur.
 */
function fallbackBitmap(): SignatureBitmap {
  return bitmapOf(SIGNATURE_FALLBACK.width, SIGNATURE_FALLBACK.height, 1)
}
